// src/modules/app-views/crm/pages/Boards/modals/BoardSettingsModal/tabs/FormFieldsTab.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useRef, useState } from 'react';
import { Button, Card, Divider, Input, Select, Tag, Tooltip } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  FileTextOutlined,
  HolderOutlined,
  KeyOutlined,
  LockOutlined,
  MailOutlined,
  PhoneOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import translateX from 'utils/translateX';
import { getFieldTypeMeta } from '../../../../../constants/formFieldTypes';
import FieldEditorModal from './FieldEditorModal';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const INTRINSIC_FIELDS = [
  { fieldName: 'name', defaultLabel: 'contact_name', icon: UserOutlined, color: 'blue' },
  { fieldName: 'uniqueKey', defaultLabel: 'unique_key', icon: KeyOutlined, color: 'cyan' },
  { fieldName: 'phone', defaultLabel: 'phone', icon: PhoneOutlined, color: 'orange' },
  { fieldName: 'email', defaultLabel: 'email', icon: MailOutlined, color: 'red' },
  { fieldName: 'opportunity', defaultLabel: 'opportunity_description', icon: FileTextOutlined, color: 'green' },
];

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const FormFieldsTab = ({
  localFormFields, setLocalFormFields, localColumns,
  newOpportunityMode, setNewOpportunityMode,
  opportunityField, setOpportunityField,
  intrinsicFieldLabels, setIntrinsicFieldLabels,
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editingIntrinsic, setEditingIntrinsic] = useState(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = localFormFields.findIndex(f => f.fieldName === active.id);
    const newIndex = localFormFields.findIndex(f => f.fieldName === over.id);
    setLocalFormFields(prev => arrayMove(prev, oldIndex, newIndex));
  };

  const handleAdd = () => {
    setEditingField(null);
    setEditorOpen(true);
  };

  const handleEdit = field => {
    setEditingField(field);
    setEditorOpen(true);
  };

  const handleDelete = fieldName => {
    setLocalFormFields(prev => {
      const updated = prev.filter(f => f.fieldName !== fieldName);
      // Force standard mode when all custom fields are removed
      if (updated.length === 0) setNewOpportunityMode('standard');
      return updated;
    });
    // Remove deleted field from opportunityField composition
    setOpportunityField(prev => (prev || []).filter(f => f !== fieldName));
  };

  const handleSave = fieldObj => {
    setLocalFormFields(prev => {
      // Editing existing field
      const idx = prev.findIndex(f => f.fieldName === fieldObj.fieldName);
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], ...fieldObj };
        return updated;
      }
      // New field — append at end
      return [...prev, { ...fieldObj, order: prev.length + 1 }];
    });
  };

  const handleIntrinsicLabelSave = (fieldName, newLabel) => {
    setIntrinsicFieldLabels(prev => ({ ...prev, [fieldName]: newLabel }));
    setEditingIntrinsic(null);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  const existingFieldNames = localFormFields.map(f => f.fieldName);
  const hasFormFields = localFormFields.length > 0;
  const fieldOptions = localFormFields.map(f => ({ value: f.fieldName, label: f.title || f.fieldName }));

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ paddingRight: 4, paddingBottom: 16 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 12 }}>

        {/* Intrinsic fields — locked, label-only edit */}
        {INTRINSIC_FIELDS.map(field => (
          <IntrinsicFieldRow
            key={field.fieldName}
            field={field}
            customLabel={intrinsicFieldLabels?.[field.fieldName]}
            isEditing={editingIntrinsic === field.fieldName}
            onStartEdit={() => setEditingIntrinsic(field.fieldName)}
            onSaveLabel={newLabel => handleIntrinsicLabelSave(field.fieldName, newLabel)}
            onCancelEdit={() => setEditingIntrinsic(null)}
          />
        ))}

        <Divider style={{ margin: '4px 0' }} />

        {/* Custom fields — draggable */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={localFormFields.map(f => f.fieldName)}
            strategy={verticalListSortingStrategy}
          >
            {localFormFields.map(field => (
              <SortableFieldRow
                key={field.fieldName}
                field={field}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </SortableContext>
        </DndContext>

        <Button
          type="dashed"
          icon={<PlusOutlined />}
          size="small"
          onClick={handleAdd}
          style={{ marginTop: 4 }}
        >
          {translateX('add_field')}
        </Button>
      </div>

      {/* New Opportunity Mode card */}
      <Card
        size="small"
        title={translateX('new_opportunity_mode')}
        style={{ marginTop: 16 }}
      >
        <div style={{ marginBottom: 6 }}>
          <span>
            {translateX('form')}
            <Tooltip title={translateX('new_opportunity_mode_tooltip')}>
              <QuestionCircleOutlined style={{ marginLeft: 4, color: '#999' }} />
            </Tooltip>
          </span>
        </div>
        <Select
          value={newOpportunityMode}
          onChange={setNewOpportunityMode}
          options={[
            { value: 'standard', label: translateX('default') },
            { value: 'custom', label: translateX('customized'), disabled: !hasFormFields },
          ]}
          style={{ width: '100%' }}
        />

        {/* Opportunity field composition */}
        <div style={{ marginTop: 16, marginBottom: 6 }}>
          <span style={{ color: newOpportunityMode === 'custom' ? 'inherit' : '#bbb' }}>
            {newOpportunityMode === 'custom' && <span style={{ color: '#ff4d4f', marginRight: 4 }}>*</span>}
            {translateX('opportunity_field')}
            <Tooltip title={translateX('opportunity_field_tooltip')}>
              <QuestionCircleOutlined style={{ marginLeft: 4, color: '#999' }} />
            </Tooltip>
          </span>
        </div>
        <Select
          mode="multiple"
          value={opportunityField}
          onChange={setOpportunityField}
          options={fieldOptions}
          placeholder={translateX('select_fields')}
          allowClear
          disabled={newOpportunityMode !== 'custom'}
          status={newOpportunityMode === 'custom' && !opportunityField?.length ? 'error' : undefined}
          style={{ width: '100%' }}
        />
      </Card>

      <FieldEditorModal
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        field={editingField}
        onSave={handleSave}
        columns={localColumns}
        existingFieldNames={existingFieldNames}
      />
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default FormFieldsTab;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function IntrinsicFieldRow({ field, customLabel, isEditing, onStartEdit, onSaveLabel, onCancelEdit }) {
  const inputRef = useRef(null);
  const Icon = field.icon;
  const displayLabel = customLabel || translateX(field.defaultLabel);

  const handleConfirm = () => {
    const val = inputRef.current?.input?.value?.trim() || '';
    // Save custom label only if different from default; empty resets to default
    onSaveLabel(val || '');
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', opacity: 0.75 }}>
      <LockOutlined style={{ fontSize: 14, opacity: 0.35, flexShrink: 0 }} />

      {isEditing ? (
        <Input
          ref={inputRef}
          size="small"
          defaultValue={customLabel || ''}
          placeholder={translateX(field.defaultLabel)}
          autoFocus
          onPressEnter={handleConfirm}
          onBlur={handleConfirm}
          style={{ flex: 1 }}
        />
      ) : (
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {displayLabel}
        </span>
      )}

      <Tooltip title={translateX('intrinsic_field')}>
        <Tag color={field.color} style={{ margin: 0, flexShrink: 0 }}>
          <Icon style={{ marginRight: 0 }} />
        </Tag>
      </Tooltip>

      <Tooltip title={translateX('rename')}>
        <Button
          type="text"
          size="small"
          icon={<EditOutlined />}
          onClick={onStartEdit}
          style={{ padding: '0 4px', flexShrink: 0 }}
        />
      </Tooltip>

      {/* Spacer matching delete button width for alignment */}
      <div style={{ width: 24, flexShrink: 0 }} />
    </div>
  );
}

function SortableFieldRow({ field, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.fieldName,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '4px 0',
  };

  const meta = getFieldTypeMeta(field.fieldType);
  const Icon = meta.icon;

  return (
    <div ref={setNodeRef} style={style}>
      <HolderOutlined
        {...attributes}
        {...listeners}
        style={{ fontSize: 14, cursor: 'grab', opacity: 0.45, flexShrink: 0 }}
      />

      <span
        style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {field.title || field.fieldName}
      </span>

      <Tag color={meta.color} style={{ margin: 0, flexShrink: 0 }}>
        <Icon style={{ marginRight: 4 }} />
        {translateX(meta.label)}
      </Tag>

      <Tooltip title={translateX('edit')}>
        <Button
          type="text"
          size="small"
          icon={<EditOutlined />}
          onClick={() => onEdit(field)}
          style={{ padding: '0 4px', flexShrink: 0 }}
        />
      </Tooltip>

      <Tooltip title={translateX('delete')}>
        <Button
          type="text"
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => onDelete(field.fieldName)}
          style={{ padding: '0 4px', flexShrink: 0 }}
        />
      </Tooltip>
    </div>
  );
}
